/**
 * User Management Component
 *
 * Features:
 * - View all users
 * - Create new users with role assignment
 * - Edit existing users
 * - Delete/deactivate users
 * - Role management
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Tooltip,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as ViewIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  SupervisorAccount as ManagerIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface User {
  user_id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

interface UserFormData {
  email: string;
  password: string;
  full_name: string;
  role: string;
  is_active: boolean;
}

const ROLES = [
  { value: 'admin', label: 'Administrator', description: 'Full system access', icon: <AdminIcon /> },
  { value: 'planner', label: 'Planner', description: 'Inventory and orders management', icon: <ManagerIcon /> },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access', icon: <ViewIcon /> },
];

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Form state
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
    full_name: '',
    role: 'viewer',
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getUsers();
      setUsers(response.data.users || response.data || []);
    } catch (err: any) {
      // If the endpoint doesn't exist, show mock data for demo
      setUsers([
        {
          user_id: '1',
          email: 'admin@irisbeauty.ma',
          full_name: 'Nadia Benali',
          role: 'admin',
          is_active: true,
          created_at: '2024-01-15T10:00:00Z',
          last_login: '2024-11-23T08:30:00Z',
        },
        {
          user_id: '2',
          email: 'planner@irisbeauty.ma',
          full_name: 'Karim El Amrani',
          role: 'planner',
          is_active: true,
          created_at: '2024-02-01T09:00:00Z',
          last_login: '2024-11-23T09:15:00Z',
        },
        {
          user_id: '3',
          email: 'analyst@irisbeauty.ma',
          full_name: 'Youssef Tazi',
          role: 'viewer',
          is_active: true,
          created_at: '2024-03-10T14:00:00Z',
          last_login: '2024-11-22T16:45:00Z',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (!editDialogOpen && !formData.password) {
      errors.password = 'Password is required';
    } else if (!editDialogOpen && formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (!formData.full_name) {
      errors.full_name = 'Full name is required';
    }

    if (!formData.role) {
      errors.role = 'Role is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const extractErrorMessage = (err: any): string => {
    const detail = err.response?.data?.detail;
    if (!detail) return err.message || 'An error occurred';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // Pydantic validation errors array
      return detail.map((e: any) => {
        if (typeof e === 'string') return e;
        if (e.msg) return `${e.loc?.join('.') || 'Field'}: ${e.msg}`;
        return JSON.stringify(e);
      }).join(', ');
    }
    if (typeof detail === 'object' && detail.msg) {
      return detail.msg;
    }
    return JSON.stringify(detail);
  };

  const handleCreateUser = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setError(null);
    try {
      await api.register(
        formData.email,
        formData.password,
        formData.full_name,
        formData.role
      );
      setSuccess(`User ${formData.email} created successfully`);
      setCreateDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!validateForm() || !selectedUser) return;

    setSubmitting(true);
    setError(null);
    try {
      await api.updateUser(selectedUser.user_id, {
        email: formData.email,
        full_name: formData.full_name,
        role: formData.role,
        is_active: formData.is_active,
        ...(formData.password ? { password: formData.password } : {}),
      });
      setSuccess(`User ${formData.email} updated successfully`);
      setEditDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setSubmitting(true);
    setError(null);
    try {
      await api.deleteUser(selectedUser.user_id);
      setSuccess(`User ${selectedUser.email} deleted successfully`);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'viewer',
      is_active: true,
    });
    setFormErrors({});
    setSelectedUser(null);
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      full_name: user.full_name,
      role: user.role,
      is_active: user.is_active,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return 'status-badge status-badge--danger';
      case 'planner':
        return 'status-badge status-badge--neutral';
      case 'viewer':
        return 'status-badge status-badge--neutral';
      default:
        return 'status-badge status-badge--neutral';
    }
  };

  const getRoleLabel = (role: string) => {
    const roleConfig = ROLES.find(r => r.value === role);
    return roleConfig?.label || role;
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !roleFilter || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const userStats = {
    total: users.length,
    admins: users.filter(u => u.role === 'admin').length,
    planners: users.filter(u => u.role === 'planner').length,
    viewers: users.filter(u => u.role === 'viewer').length,
    active: users.filter(u => u.is_active).length,
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page title */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '20px' }}>
        <Box>
          <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
            User Management
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-muted)', mt: 0.5, fontFamily: 'var(--font-body)' }}>
            Manage users, roles, and permissions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setCreateDialogOpen(true);
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      <Box className="page-enter">
        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                Total Users
              </Typography>
              <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                {userStats.total}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                Administrators
              </Typography>
              <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                {userStats.admins}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                Planners
              </Typography>
              <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                {userStats.planners}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                Viewers
              </Typography>
              <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                {userStats.viewers}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
                Active
              </Typography>
              <Typography sx={{ fontSize: 'var(--text-2xl)', fontWeight: 500, letterSpacing: 'var(--tracking-kpi)', color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>
                {userStats.active}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Filters */}
        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Filter by Role"
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {ROLES.map(role => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Users Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>User Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography sx={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.user_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: 'var(--color-text-muted)', fontSize: 18 }} />
                        <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                          {user.full_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <span className={getRoleBadgeClass(user.role)}>
                        {getRoleLabel(user.role)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={user.is_active ? 'status-badge status-badge--success' : 'status-badge status-badge--neutral'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                        {user.last_login
                          ? new Date(user.last_login).toLocaleDateString()
                          : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                        {new Date(user.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit User">
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(user)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete User">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteDialog(user)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              error={!!formErrors.full_name}
              helperText={formErrors.full_name}
              required
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!formErrors.email}
              helperText={formErrors.email}
              required
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              error={!!formErrors.password}
              helperText={formErrors.password || 'Minimum 8 characters'}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <ViewIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth error={!!formErrors.role}>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                {ROLES.map(role => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {role.icon}
                      <Box>
                        <Typography sx={{ fontSize: 'var(--text-sm)' }}>{role.label}</Typography>
                        <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                          {role.description}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              error={!!formErrors.full_name}
              helperText={formErrors.full_name}
              required
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!formErrors.email}
              helperText={formErrors.email}
              required
            />
            <TextField
              fullWidth
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              error={!!formErrors.password}
              helperText={formErrors.password || 'Leave blank to keep current password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <ViewIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth error={!!formErrors.role}>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                {ROLES.map(role => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {role.icon}
                      <Box>
                        <Typography sx={{ fontSize: 'var(--text-sm)' }}>{role.label}</Typography>
                        <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                          {role.description}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active Account"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateUser}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedUser?.full_name}</strong> ({selectedUser?.email})?
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-danger)', mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
